import { useState, useMemo, useEffect, memo, useCallback, useRef } from 'react';
import { useShellRouter } from '@/hooks/useShellRouter';
import DOMPurify from 'dompurify';
import { Button, EmptyState } from '@syia-ai/maritimeai-ui';
import AppLoader from '@/components/AppLoader';
import { registerIframe, unregisterIframe } from '../lib/vessel-bridge';
import { logAccess, reportError } from '@/observability/clientLogger';

interface OptimizedIframeProps {
  url: string;
  title: string;
  embedCode?: string; // Raw HTML embed code (Power BI, Google Sheets, etc.)
  type?: string;      // 'iframe' or 'embed'
}

// Sanitization config — only allow embed-related tags, strip scripts
const ALLOWED_TAGS = ['iframe', 'embed', 'object', 'param', 'source'];
const ALLOWED_ATTR = [
  'src', 'width', 'height', 'frameborder', 'allow', 'allowfullscreen',
  'title', 'type', 'data', 'name', 'value', 'sandbox', 'loading',
  'referrerpolicy', 'style', 'class', 'scrolling',
];

const OptimizedIframe = memo(function OptimizedIframe({ url, title, embedCode, type }: OptimizedIframeProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const router = useShellRouter();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const iframeId = useRef(`iframe-${crypto.randomUUID()}`);
  // Sanitize embed code if provided
  const sanitizedEmbed = useMemo(() => {
    if (!embedCode || typeof window === 'undefined') return '';
    return DOMPurify.sanitize(embedCode, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      ADD_TAGS: ['iframe'],
      ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling'],
    });
  }, [embedCode]);

  // Ref callback to attach load/error handlers to embed iframes
  const embedContainerRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    const iframe = node.querySelector('iframe');
    if (iframe) {
      iframe.style.width = '100%';
      iframe.style.height = '100vh';
      iframe.style.border = 'none';
      iframe.style.borderRadius = '8px';
      iframe.onload = () => setIsLoading(false);
      iframe.onerror = () => { setIsLoading(false); setError('Failed to load embed content'); };
    } else {
      setIsLoading(false);
    }
  }, []);

  // Get authToken from localStorage and append to URL (only for iframe mode)
  const iframeUrl = useMemo(() => {
    if (embedCode) return ''; // Not needed for embed mode
    if (typeof window === 'undefined') return url;

    const authToken = localStorage.getItem('authToken');

    if (!authToken) {
      // The sub-app iframe is about to load without a credential and will show
      // its own "not signed in" state — which reads to the user as that App being
      // broken rather than the shell having no token.
      reportError(new Error('No authToken in localStorage; iframe loads unauthenticated'), {
        event_detail: 'iframe_no_auth_token',
      });
      return url;
    }

    try {
      const urlObj = new URL(url);
      urlObj.searchParams.set('authToken', authToken);
      urlObj.searchParams.set('from', 'microapps');

      // vesselSync_activeVessel covers ALL vessel types (including individual VESSEL).
      // Fall back to vesselDropdown_activeGroup for backwards compat.
      let syncId = '', syncName = '', syncLevel = '', syncImoList: (string | number)[] = [];
      try {
        const raw = localStorage.getItem('vesselSync_activeVessel');
        if (raw) {
          const sync = JSON.parse(raw);
          if (sync?.imoList?.length > 0) {
            syncId = sync.id ?? '';
            syncName = sync.vesselName ?? '';
            syncLevel = sync.level ?? '';
            syncImoList = sync.imoList;
          }
        }
      } catch {}
      if (!syncImoList.length) {
        try {
          const raw = localStorage.getItem('vesselDropdown_activeGroup');
          if (raw) {
            const group = JSON.parse(raw);
            if (group?.imoList?.length > 0) {
              syncId = group.id ?? '';
              syncName = group.vesselName ?? '';
              syncLevel = group.level ?? '';
              syncImoList = group.imoList;
            }
          }
        } catch {}
      }
      if (syncImoList.length > 0) {
        urlObj.searchParams.set('vesselId', syncId);
        urlObj.searchParams.set('vesselName', syncName);
        urlObj.searchParams.set('vesselLevel', syncLevel);
        urlObj.searchParams.set('imoList', syncImoList.join(','));
      } else {
        // The App mounts with no vessel selected. Indistinguishable, from
        // inside that App, from a user who has not picked one yet.
        reportError(new Error('No vessel in localStorage; iframe loads without vessel sync'), {
          event_detail: 'iframe_no_vessel_sync',
        });
      }

      return urlObj.toString();
    } catch (error) {
      reportError(error, { event_detail: 'iframe_url_parse_failed' });
      return url;
    }
  }, [url, embedCode]);

  // Handle iframe load
  const handleLoad = () => {
    setIsLoading(false);
    setError(null);
  };

  // Handle iframe errors
  const handleError = () => {
    setIsLoading(false);
    setError('Failed to load content');
  };

  // Register this iframe with vessel-bridge so it receives VESSEL_CHANGED broadcasts
  // and its vessel_changed messages are handled + re-broadcast to other iframes.
  useEffect(() => {
    registerIframe(iframeId.current, iframeRef);
    return () => unregisterIframe(iframeId.current);
  }, []);

  // The shell's defining interaction: a user opened an App. One line per mount,
  // in the effect rather than the component body, so it fires once and not on
  // every re-render. `title` and not the URL — the URL carries the auth token
  // and the vessel sync params.
  useEffect(() => {
    logAccess('shell_app_opened', { app: title, embedded: !!embedCode });
  }, [title, embedCode]);

  // Validate — need either a valid URL or embed code
  const isEmbed = !!embedCode && !!sanitizedEmbed;
  if (!isEmbed && (!iframeUrl || iframeUrl === 'about:blank')) {
    return (
      <div className="shell-frame-box">
        <EmptyState
          className="shell-frame-box__overlay"
          headingLevel={2}
          title="This page can't be shown"
          description={`Invalid URL: ${iframeUrl || 'undefined'}`}
          actions={<Button onClick={() => router.push('/')}>Go Home</Button>}
        />
      </div>
    );
  }

  return (
    <div className="shell-frame-box">
      {isLoading && <AppLoader fill="overlay" label={`Loading ${title}`} />}

      {error && (
        <EmptyState
          className="shell-frame-box__overlay"
          headingLevel={2}
          title="This page didn't load"
          description={error}
          actions={<Button onClick={() => router.push('/')}>Go Home</Button>}
        />
      )}

      {isEmbed ? (
        <div
          ref={embedContainerRef}
          className="shell-frame-box__embed"
          dangerouslySetInnerHTML={{ __html: sanitizedEmbed }}
        />
      ) : type === 'embed' && iframeUrl ? (
        <embed
          src={iframeUrl}
          type="text/html"
          title={title}
          className="shell-iframe-fullscreen"
          onLoad={handleLoad}
          onError={handleError}
        />
      ) : (
        <iframe
          ref={iframeRef}
          src={iframeUrl}
          title={title}
          className="shell-iframe-fullscreen"
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-popups-to-escape-sandbox allow-downloads"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          referrerPolicy="strict-origin-when-cross-origin"
          onLoad={handleLoad}
          onError={handleError}
        />
      )}

    </div>
  );
});

export default OptimizedIframe;
